import React, { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { gsap } from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import Lenis from "lenis";
import Header from "./Header";
import { styled, Theme, useMediaQuery } from "@mui/system";
// import PopupForm from './PopupForm';
import Footer from "./FooterContainer";
import CallIcon from '@mui/icons-material/Call';
import LeadModal from "./LeadModal";
import { Helmet } from 'react-helmet-async';
import MasterLayout from "./MasterLayoutNew";
import HeroSectionMain from "./HeroSectionMain";
import AboutUsNew from "./AboutUsNew";
import ConnectedLiving from "./ConntedLivingNew";
import FloorPlansNew from "./FloorPlanNew";
import BottomNav from "./BottomNav";
import Cookies from "js-cookie"; // Import js-cookie for cookie management

gsap.registerPlugin(ScrollToPlugin);

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  right: "27px",
  top: "30%",
  transform: "translateY(-50%) rotate(-90deg)",
  transformOrigin: "right center",
  zIndex: 1000,

  [theme.breakpoints.down("sm")]: {
    right: "20px",
    top: "30%",
  },
}));

const AnimatedButton = styled(Button)({
  bgcolor: "#9f8054",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "14px",
  letterSpacing:'1px',
  padding: "15px 20px",
  borderRadius: "10px 10px 0px 0px",
  boxShadow: "0px 4px 15px rgba(159, 128, 84, 0.5)",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  zIndex: 100000,
  border: "1.5px solid #ffffff",
  transition: "transform 0.3s ease, box-shadow 0.3s",
  "&:hover": {
    bgcolor: "#7f6540",
    transform: "scale(1.05)",
    boxShadow: "0px 8px 25px rgba(159, 128, 84, 0.8)",
  },
  animation: "pulse 2s infinite", 
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
    "50%": { boxShadow: "0 0 25px rgba(159, 128, 84, 0.8)" },
    "100%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
  },
});

const ProjectsPage: React.FC = () => {
  const [index, setIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [formFilled, setFormFilled] = useState<boolean>(false); // Track if the form is filled
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const sectionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize Lenis smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Smooth easing
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy(); // Clean up Lenis instance
    };
  }, []);

  useEffect(() => {
    // Scroll to the top of the page on mount
    gsap.to(window, { duration: 0, scrollTo: 0 });
  }, []);

  useEffect(() => {
    // Check if the form is already filled from cookies
    const isFormFilled = Cookies.get("formFilled") === "true";
    setFormFilled(isFormFilled);

    if (!isFormFilled) {
      // Open modal after 25 seconds
      const initialTimer = setTimeout(() => {
        if (!isOpen) {
          setIsOpen(true);
        }
      }, 25000);

      return () => clearTimeout(initialTimer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!formFilled) {
      // Reopen modal every 30 seconds after it is closed
      let reopenTimer: NodeJS.Timeout | null = null;

      if (!isOpen) {
        reopenTimer = setTimeout(() => {
          if (!isOpen) {
            setIsOpen(true);
          }
        }, 30000);
      }

      return () => {
        if (reopenTimer) clearTimeout(reopenTimer);
      };
    }
  }, [isOpen, formFilled]);

  return (
    <>
      <div ref={sectionsRef}>
        <Helmet>
          {/* ✅ Title - High SEO Impact */}
          <title>4 BHK Flats for Sale in Hyderabad | Luxury Apartments  </title>

          {/* ✅ Meta Description - 160 Characters Max for Google */}
          <meta 
            name="description" 
            content="Explore spacious 4 BHK flats for sale in Hyderabad. Ready-to-move luxury apartments in prime locations. Book your dream home today!" 
          />

          {/* ✅ SEO Keywords - Focus on High Search Volume Terms */}
          <meta 
            name="keywords" 
            content="4 BHK ready to move flats in Hyderabad, luxury 4 BHK apartments in Hyderabad, under construction 4 BHK flats Hyderabad, spacious 4 BHK flats in Hyderabad, 4 BHK flats in Gachibowli Hyderabad, 4 BHK flats in Kondapur Hyderabad, 4 BHK apartments for NRI buyers in Hyderabad, 4 BHK gated community flats Hyderabad" 
          />

          {/* ✅ Author */}
          <meta name="author" content="Kolla Group" />

          {/* ✅ Open Graph (OG) - Facebook, LinkedIn, WhatsApp */}
          <meta property="og:title" content="4 BHK Flats for Sale in Hyderabad | Luxury Apartments " />
          <meta property="og:description" content="Explore spacious 4 BHK flats for sale in Hyderabad. Ready-to-move luxury apartments in prime locations. Book your dream home today!" />
          <meta property="og:image" content="https://kollagroup.com/kollaluxuria.png" />
          <meta property="og:url" content="https://www.kollagroup.com/luxuria" />
          <meta property="og:type" content="website" />

          {/* ✅ Twitter Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="4 BHK Flats for Sale in Hyderabad | Luxury Apartments " />
          <meta name="twitter:description" content="Explore spacious 4 BHK flats for sale in Hyderabad. Ready-to-move luxury apartments in prime locations. Book your dream home today!" />
          <meta name="twitter:image" content="https://kollagroup.com/kollaluxuria.png" />

          {/* ✅ Canonical Tag - Prevents Duplicate SEO Issues */}
          <link rel="canonical" href="https://www.kollagroup.com/luxuria" />

          {/* ✅ Robots Meta Tag - Ensures Proper Indexing */}
          <meta name="robots" content="index, follow" />

          {/* ✅ Viewport for Mobile Optimization */}
          <meta name="viewport" content="width=device-width, initial-scale=1" />

          {/* ✅ Favicon - Branding */}
          <link rel="icon" href="https://www.kollagroup.com/favicon.ico" />

          {/* ✅ Hreflang - Future-Ready for International Targeting */}
          {/* <link rel="alternate" href="https://www.kollagroup.com/luxuria" hreflang="en-IN" /> */}

          {/* ✅ JSON-LD Schema Markup for Rich Snippets */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Kolla Luxuria",
              "url": "https://www.kollagroup.com/luxuria",
              "description": "Kolla Luxuria is a premium luxury residential project offering 4 BHK Villas & Apartments in Kondapur, Hyderabad.",
              "publisher": {
                "@type": "Organization",
                "name": "Kolla Group",
                "logo": "https://www.kollagroup.com/favicon.ico"
              }
            })}
          </script>
        </Helmet>

        <div style={{ display: "none"}}>
          <h1>4 BHK Flats for Sale in Hyderabad</h1>
          <h2>Premium 4 BHK Apartments in Prime Locations</h2>
          <h2>Under-Construction and Ready-to-Move Options</h2>
          <h3>Why Choose a 4 BHK Flat in Hyderabad?</h3>
          <h3>Top Localities Offering 4 BHK Flats</h3>
          <p>
            Looking for 4 BHK flats for sale in Hyderabad? You're in the right place. Whether you prefer a high-rise apartment with skyline views or a gated community with world-class amenities, Hyderabad offers a range of luxurious and spacious 4 BHK apartments across top localities like Gachibowli, Kondapur, Jubilee Hills, and Kokapet.
            From ready-to-move-in homes to under-construction projects, buyers can choose based on budget and possession timeline. These homes are perfect for large families, those working from home, or anyone seeking space, privacy, and comfort in a premium setting.
            Explore options with modern amenities such as clubhouses, swimming pools, co-working spaces, and smart home features. Hyderabad’s real estate market continues to grow, making it the right time to invest in a 4 BHK flat.
          </p>
          <a href="https://kollagroup.com/luxor-exclusive">Luxor</a>
          <a href="https://kollagroup.com/luxuria">Luxuria</a>
          <a href="https://kollagroup.com/contactus">Contact</a>
          <a href="https://kollagroup.com/">Home</a>
          <a href="https://www.instagram.com/kolla_group/">Instagram</a>
          <a href="https://www.facebook.com/kollahousing">Facebook</a>
          <a href="https://www.youtube.com/@KOLLA_Group">YouTube</a>
          <a href="https://www.linkedin.com/company/kolla_group/">LinkedIn</a>
        </div>


        <Header />

        <Box component="section" id="Home" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <HeroSectionMain />
        </Box>

        <Box component="section" id="AboutUs" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <AboutUsNew />
        </Box>

        <Box component="section" id="Layout" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <MasterLayout />
        </Box>

        <Box component="section" id="FloorPlans" className="section" sx={{ maxHeight: isMobile ? "auto" : "100vh" }}>
          <FloorPlansNew />
        </Box>

        <Box component="section" id="Location" className="section" sx={{ maxHeight: isMobile ? "auto" : "100vh" }}>
          <ConnectedLiving />
        </Box>

        <Box component="section" id="Contact" className="section" sx={{ minHeight: "auto" }}>
          <Footer />
        </Box>

        {!isMobile ? (
          <ButtonContainer>
            <AnimatedButton ref={buttonRef} variant="contained" onClick={() => setIsOpen(true)}>
              <CallIcon />
              Contact Us
            </AnimatedButton>
          </ButtonContainer>
        ) : (
          <BottomNav />
        )}

        <LeadModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
      </div>
    </>
  );
};

export default ProjectsPage;













